import {
  FlowNodeEntity,
} from '@flowgram.ai/free-layout-editor';
import { roboneoApi } from '$/api/request';
import APP from '$/api/api';
import useFlowStore from '$/store/useFlowStore';

export interface UnfinishDescTask {
  task_id: string;
  media_url: string;
  currentTime: number;
}

/**
 * 轮询查询媒体描述任务结果
 *
 * @param {string} taskId
 * @param {(res: string) => void} resolve
 * @param {(err: unknown) => void} reject
 * @param {FlowNodeEntity} node 节点实例，用于清除未完成任务
 */
async function pollingQueryDescriptionTask(
  taskId: string,
  resolve: (res: string) => void,
  reject: (err: unknown) => void,
  node: FlowNodeEntity,
) {
  try {
    const response = await roboneoApi.request({
      data: {
        parameter: {
          path_scene: 'nodeexecutequery',
          task_ids: [taskId],
        },
      },
      method: 'POST',
      url: APP.roboneo.api.path,
    });

    if (response.data.error_code !== 0) {
      reject(response.data);
      return;
    }

    const taskItem = response.data.parameter.tasks[taskId];
    const { output: outputStr, state } = taskItem.steps[0];

    // 成功
    if (state === 'SUCCESS') {
      try {
        const output = JSON.parse(outputStr);
        const result = output?.data?.result?.parameters?.result?.value?.result?.gen_text || '';
        // 清空未完成的任务
        clearUnfinishDescTaskList(node);
        resolve(result);
      } catch {
        clearUnfinishDescTaskList(node);
        resolve('');
      }
      return;
    }

    // 失败
    if (state === 'FAIL') {
      // 清空未完成的任务
      clearUnfinishDescTaskList(node);
      reject({ error_message: taskItem.error_message || '任务执行失败' });
      return;
    }

    // 轮询
    setTimeout(() => {
      pollingQueryDescriptionTask(taskId, resolve, reject, node);
    }, 2000);
  } catch (error) {
    // 轮询
    setTimeout(() => {
      pollingQueryDescriptionTask(taskId, resolve, reject, node);
    }, 2000);
  }
}

/**
 * 清空节点上的未完成描述任务列表
 */
function clearUnfinishDescTaskList(node: FlowNodeEntity) {
  const data = node.toJSON()?.data;
  const { updateFormValues } = node.form!;
  if (!updateFormValues) return;
  updateFormValues({
    ...data,
    unfinishDescTaskList: [],
  });
}

/**
 * 获取媒体内容描述
 *
 * @param {string} mediaUrl 媒体URL
 * @param {FlowNodeEntity} node 节点实例，用于保存未完成任务
 * @returns {Promise<string>} 描述文本
 */
export async function getMediaContentDescription(
  mediaUrl: string,
  node: FlowNodeEntity,
  passThroughTaskId?: string,
): Promise<string> {
  const { roomId } = useFlowStore.getState();

  const params = {
    tool_abstract_name: { cn: '', en: '' },
    name: 'media_content_descript',
    parameters: {
      media_url: mediaUrl,
    },
  };

  const parameter: any = {
    path_scene: 'nodeexecute',
    room_id: roomId,
    node_list_array: [[params]],
    pass_through_task_id: passThroughTaskId,
  };

  const response = await roboneoApi.request({
    data: {
      parameter,
    },
    method: 'POST',
    url: APP.roboneo.api.path,
  });

  return new Promise<string>((resolve, reject) => {
    if (response.data.error_code === 0) {
      const taskData = response.data.parameter.tasks[0];
      const data = node.toJSON()?.data;
      const { updateFormValues } = node.form!;
      // 缓存未完成的任务到节点表单数据
      updateFormValues({
        ...data,
        unfinishDescTaskList: [{
          task_id: taskData.task_id,
          media_url: mediaUrl,
          currentTime: Date.now(),
          passThroughTaskId,
        }],
      });
      // 开始轮询
      pollingQueryDescriptionTask(taskData.task_id, resolve, reject, node);
    } else {
      reject(response.data);
    }
  });
}

/**
 * 恢复未完成的媒体描述任务
 * 页面刷新后调用此函数继续执行未完成的任务
 *
 * @param {FlowNodeEntity} node 节点实例
 * @param {Record<string, any>} data 节点表单数据
 * @returns {Promise<string>} 描述文本，如果没有未完成任务则返回空字符串
 */
export function resumeUnfinishDescriptionTask(
  node: FlowNodeEntity,
  data: Record<string, any>,
): Promise<string> {
  return new Promise((resolve, reject) => {
    // 如果没有未完成的任务，直接返回空字符串
    if (!data?.unfinishDescTaskList?.length) {
      resolve('');
      return;
    }

    const { task_id } = data.unfinishDescTaskList[0];
    // 开始轮询
    pollingQueryDescriptionTask(task_id, resolve, reject, node);
  });
}
